import { useState } from 'react';
import { ConsolePage, DataTable, Icon, Modal, Panel, StatusBadge, Td, TypeBadge, QueryState, ListSkeleton, useToast, type Column } from '@/components';
import { useCourses, useUpdateCourse } from '@/hooks/queries';
import { useLocalized } from '@/hooks/useLocalized';
import type { Course } from '@/types/domain';

const COLS: Column[] = [
  { label: 'Course' },
  { label: 'Host' },
  { label: 'Type' },
  { label: 'Enrolled', align: 'center' },
  { label: 'Capacity', align: 'center' },
  { label: 'Status' },
  { label: '' },
];

export function AdminCourses() {
  const { L } = useLocalized();
  const coursesQuery = useCourses();
  const courses = coursesQuery.data ?? [];
  const [editing, setEditing] = useState<Course | null>(null);

  return (
    <ConsolePage title="Courses" th="จัดการคอร์ส" crumbs="RECORDS" maxWidth={1140} actions={<button className="btn btn-primary btn-sm"><Icon name="plus" size={15} /> New course</button>}>
      <QueryState query={coursesQuery} skeleton={<ListSkeleton rows={6} />} isEmpty={() => courses.length === 0} emptyIcon="book" emptyTitle="No courses yet">
        {() => (
          <Panel pad={false}>
        <DataTable
          cols={COLS}
          rows={courses}
          renderRow={(c) => (
            <>
              <Td>
                <div style={{ minWidth: 0 }}>
                  <div className="th" style={{ fontWeight: 600 }}>{L(c.title)}</div>
                  <div className="muted" style={{ fontSize: 12 }}>{c.title.en} · {c.code}</div>
                </div>
              </Td>
              <Td><span style={{ fontSize: 13 }}>{c.host}</span></Td>
              <Td><TypeBadge type={c.type} /></Td>
              <Td align="center"><span className="u-mono">{c.enrolled}</span></Td>
              <Td align="center"><span className="u-mono muted">{c.capacity}</span></Td>
              <Td><StatusBadge status={c.status} /></Td>
              <Td align="center"><button className="btn-quiet btn-sm" title="Edit organizer" style={{ border: 'none', background: 'transparent', cursor: 'pointer' }} onClick={() => setEditing(c)}><Icon name="pencil" size={16} /></button></Td>
            </>
          )}
        />
        </Panel>
        )}
      </QueryState>
      {editing && <EditOrganizerModal course={editing} onClose={() => setEditing(null)} />}
    </ConsolePage>
  );
}

/** Superadmin edit of a course's organizer (host). Drives the organizer shown
 *  on NEWLY-issued certificates; already-issued certs keep their snapshotted
 *  host, so this never rewrites credentials that are already out. */
function EditOrganizerModal({ course, onClose }: { course: Course; onClose: () => void }) {
  const { toast } = useToast();
  const update = useUpdateCourse();
  const [host, setHost] = useState(course.host);
  const trimmed = host.trim();

  const save = () => {
    if (!trimmed) return;
    update.mutate(
      { id: course.id, patch: { host: trimmed } },
      {
        onSuccess: () => { toast('Organizer updated', { icon: 'check' }); onClose(); },
        onError: () => toast('Could not save organizer', { tone: 'clay', icon: 'x' }),
      },
    );
  };

  return (
    <Modal open onClose={onClose} title="Edit organizer" width={420}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.5 }}>
          The organizing body shown on certificates for <strong>{course.code}</strong>.
          Applies to certificates issued from now on — already-issued certificates are unchanged.
        </p>
        <label className="field">
          <span className="label">Organizer · ผู้จัดอบรม</span>
          <input className="input" value={host} onChange={(e) => setHost(e.target.value)} />
        </label>
        <button className="btn btn-primary btn-block" disabled={update.isPending || !trimmed || trimmed === course.host} onClick={save}>
          {update.isPending ? 'Saving…' : 'Save organizer'}
        </button>
      </div>
    </Modal>
  );
}
